import React, {useEffect, useState} from "react";
import 'react-toastify/dist/ReactToastify.css';
import { useLocation } from 'react-router-dom';
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import {Modal} from "react-bootstrap";
import API from "../../services/SchoolClassService";
import SchoolClassForm from "./SchoolClassForm";
import {CLASS_TYPES, ROLES, STATUS, WEEKDAYS} from "../common/Const";
import {mandatory, validateValues} from "../common/Validations";
import {history} from "../../redux/store/configureStore";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as bookActions from "../../redux/actions/bookActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as clientActions from "../../redux/actions/clientActions";
import {
  findDayOfWeek,
  getById,
  hasErrors,
  hasNoErrors,
  minutesToTimeString,
  timeStringToMinutes,
  getStudentsInThisClass,
} from "../common/Helpers";
import LessonsList from "./LessonsList";
import StudentsList from "./StudentsList";
import ListItemSelector from "../common/ListItemSelector";
import {
  findOverlapSchoolClass as findOverlappingSchoolClass,
  saveSchoolClassWithAttendanceGuard,
} from "./schoolClassScheduling";
import {
  buildManageRouteWithSchoolYear,
} from "./manageSchoolYearRouting";
import { buildSchoolYearRange } from "../common/schoolYear";

const validationConfig = {
  classTypeId: {
    label: "Typ zajęć",
    validate: [mandatory],
  },
  dayOfWeek: {
    label: "Dzień tygodnia",
    validate: [mandatory],
  },
  startTime: {
    label: "Początek zajęć",
    validate: [mandatory],
  },
  teacherId: {
    label: "Nauczyciel",
    validate: [mandatory],
  },
};

function getName(person) {
  return person ? `${person.firstName || ''} ${person.lastName || ''}` : '';
}

export function SchoolClass({
  schoolClasses, loadSchoolClasses, teachers, loadTeachers, saveSchoolClass, scheduledLessons,
  students, loadStudents, loadClassStudents, studentsInThisClass, studentsNotInThisClass,
  addClassStudent, removeClassStudent, saveLessonRegister, loadLessonRegisters, deleteLessonRegister, books, loadBooks,
  clients, loadClients,
  activeSchoolYearStartYear,
  userRole,
  userTeacherId,
  ...props
}) {
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const isTeacher = userRole === ROLES.TEACHER;
  const canManageClass = !isTeacher;
  // eslint-disable-next-line react/destructuring-assignment
  const [schoolClass, setSchoolClass] = useState({ ...props.schoolClass });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(STATUS.VIEW);
  const [showSelectStudentModal, setShowSelectStudentModal] = useState(false);
  const [dateReadOnly, setDateReadOnly] = useState(false);


  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading schoolClasses failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    setSchoolClass({...props.schoolClass});
    if (props.schoolClass.id) {
      setStatus(STATUS.VIEW);
    } else {
      setStatus(STATUS.IDLE);
    }
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.schoolClass]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        alert(`Loading teachers failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading classStudents failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (isTeacher || students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed ${error}`);
      });
    }
  }, [isTeacher, loadStudents]);

  useEffect(() => {
    if (canManageClass && books.length === 0) {
      loadBooks().catch((error) => {
        alert(`Loading books failed ${error}`);
      });
    }
  }, [books.length, canManageClass, loadBooks]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading lessonRegisters failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    if (canManageClass && clients.length === 0) {
      loadClients().catch((error) => {
        alert(`Loading clients failed ${error}`);
      });
    }
  }, [canManageClass, clients.length, loadClients]);

  useEffect(() => {
    let hasNonEmptyLessonRegister = false;

    scheduledLessons.forEach((lesson) => {
      if (lesson.lessonRegister !== null) {
        hasNonEmptyLessonRegister = true;
      }
    });

    setDateReadOnly(hasNonEmptyLessonRegister);
  }, [scheduledLessons]);

  function getSchoolClassName(aSchoolClass) {
    const {startTime, dayOfWeek, classTypeId} = aSchoolClass;
    const dayName = findDayOfWeek(dayOfWeek).name;
    const selectedClassType = CLASS_TYPES.find(value => value.id === classTypeId);
    const startTimeMinutes = timeStringToMinutes(startTime);
    const endTime = (selectedClassType && startTimeMinutes) ? minutesToTimeString(startTimeMinutes + selectedClassType.length) : "";
    return `${dayName} ${selectedClassType.name} ${startTime}-${endTime}`;
  }

  function findOverlapSchoolClass() {
    return findOverlappingSchoolClass(schoolClass, schoolClasses);
  }

  const validate = () => {
    const {isRecurring, dayOfWeek, fromDate, tillDate} = schoolClass;
    const fromDateDayOfWeek = WEEKDAYS.find(d => d.day === (new Date(fromDate)).getDay());
    // eslint-disable-next-line no-shadow
    const errors = validateValues(schoolClass, validationConfig);
    if (!fromDate) {
      errors.fromDate = "Pole jest obowiązkowe";
    }
    if (!isRecurring && tillDate !== fromDate) {
      setSchoolClass({...schoolClass, tillDate: fromDate});
    }
    if (!isRecurring && dayOfWeek && fromDate && fromDateDayOfWeek.id !== dayOfWeek) {
      errors.dayOfWeek = "Dzień tygodnia nie zgadza się z datą";
    }
    if (isRecurring && !tillDate) {
      errors.tillDate = "Pole jest obowiązkowe";
    }
    if (isRecurring && fromDate && tillDate && fromDate > tillDate) {
      errors.fromDate = "Pierwszy tydzień musi być przed ostatnim";
      errors.tillDate = "Pierwszy tydzień musi być przed ostatnim";
    }
    // check teacher does not have any other class at the same time
    const overlapped = findOverlapSchoolClass();
    if (overlapped) {
      const message = `Zajęcia się pokrywają z ${getSchoolClassName(overlapped)}`;
      errors.saveError = message;
    }
    // check that if class is not recurrent than fromDate must be equal to tillDate
    // check that if class is  recurrent than fromDate must be before tillDate and both must be populated
    setErrors(errors);
    return hasNoErrors(errors);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validate();
  };


  const location = useLocation();

  const handleCancel = () => {
    if (status === STATUS.VIEW || !schoolClass.id) {
      const fallbackPath = location.state ? location.state.from : '/manage/classes';
      history.push(buildManageRouteWithSchoolYear(fallbackPath, location.search, activeSchoolYearStartYear));
    } else {
      setSchoolClass(props.schoolClass);
      setStatus(STATUS.VIEW);
    }
  };

  // const handleCancel = () => {
  //   if (status === STATUS.VIEW || !schoolClass.id) {
  //     history.push('/manage/classes');
  //   } else {
  //     setSchoolClass(props.schoolClass);
  //     setStatus(STATUS.VIEW);
  //   }
  // };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSchoolClass(prevClass => ({...prevClass, [name]: value}));
    validate();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      // eslint-disable-next-line no-alert
      const confirmAttendanceBackfill = () => window.confirm(
        "Zmienił się dzień tygodnia lub godzina rozpoczęcia zajęć. OK: oznacz przeszłe nieuzupełnione lekcje jako nieodbyte. Anuluj: zapisz plan bez uzupełniania tych lekcji.",
      );

      saveSchoolClassWithAttendanceGuard({
        originalSchoolClass: props.schoolClass,
        schoolClass,
        scheduledLessons,
        saveLessonRegister,
        deleteLessonRegister,
        saveSchoolClass,
        confirmAttendanceBackfill,
      })
        .then((savedClass) => {
          setStatus(STATUS.VIEW);
          if (!schoolClass.id) {
            history.push(buildManageRouteWithSchoolYear(`/manage/class/${savedClass.id}`, location.search, activeSchoolYearStartYear));
          }
        })
        .catch((error) => {
          setErrors({...errors, saveError: error.message});
          setStatus(STATUS.SUBMITTED);
        });
    } else {
      setStatus(STATUS.SUBMITTED);
    }
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then(() => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleEdit = () => {
    setStatus(STATUS.IDLE);
  };

  const handleAddStudent = (student) => {
    const classStudent = {schoolClassId: schoolClass.id, studentId: student.value};
    addClassStudent(classStudent)
      .then(() => {
        setShowSelectStudentModal(false);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
      });
  };

  const handleRemoveStudent = (student) => {
    const {classStudent} = student;
    removeClassStudent(classStudent)
      .then(() => {
        setShowSelectStudentModal(false);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
      });
  };

  return (
    <div className="mb-5">
      { hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}>{errors[key]}</div>)}
        </div>
      )}

      <SchoolClassForm
        classTypes={CLASS_TYPES}
        teachers={teachers}
        weekdays={WEEKDAYS}
        books={books}
        id={schoolClass.id}
        teacherId={schoolClass.teacherId}
        classTypeId={schoolClass.classTypeId}
        bookId={schoolClass.bookId}
        dayOfWeek={schoolClass.dayOfWeek}
        comment={schoolClass.comment}
        startTime={schoolClass.startTime}
        isRecurring={schoolClass.isRecurring}
        fromDate={schoolClass.fromDate}
        tillDate={schoolClass.tillDate}
        touched={touched}
        errors={errors}
        onBlur={handleBlur}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onEdit={handleEdit}
        onCancel={handleCancel}
        status={status}
        dateReadOnly={dateReadOnly}
        canEdit={canManageClass}
      />

      <Modal show={showSelectStudentModal} onHide={() => setShowSelectStudentModal(false)} centered>
        <Modal.Body>
          <div className="modal-title mb-2">Wybierz ucznia</div>
          <ListItemSelector label="" onSelect={handleAddStudent} options={studentsNotInThisClass} />
        </Modal.Body>
      </Modal>

      {status === STATUS.VIEW && (
        <StudentsList
          students={studentsInThisClass}
          onViewParent={() => {}}
          onEdit={null}
          onRemove={null}
          onAddStudent={null}
          hideClientIdFallback={isTeacher}
        />
      )}
      {status !== STATUS.VIEW && schoolClass.id && canManageClass && (
        <StudentsList students={studentsInThisClass} onViewParent={() => {}} onEdit={null} onRemove={handleRemoveStudent} onAddStudent={() => setShowSelectStudentModal(true)}/>
      )}

      { schoolClass.id && (
        <LessonsList
          scheduledLessons={scheduledLessons}
          students={students}
          teachers={teachers}
          onSubmit={handleRegisterUpdate}
          deleteLessonRegister={deleteLessonRegister}
          role={userRole}
          userTeacherId={userTeacherId}
        />
      )}
    </div>
  );
}

SchoolClass.propTypes = {
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  schoolClass: PropTypes.object.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  saveSchoolClass: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  scheduledLessons: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  loadStudents: PropTypes.func.isRequired,
  addClassStudent: PropTypes.func.isRequired,
  removeClassStudent: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  studentsInThisClass: PropTypes.array.isRequired,
  studentsNotInThisClass: PropTypes.array.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  books: PropTypes.array.isRequired,
  loadBooks: PropTypes.func.isRequired,
  clients: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
};

SchoolClass.defaultProps = {
  userRole: "",
  userTeacherId: "",
};

function getStudentsNotInThisClass(students, classStudents, schoolClassId, clients) {
  const studentIds = classStudents.filter(cs => cs.schoolClassId === schoolClassId).map(cs => cs.studentId);
  return students.filter(s => !studentIds.includes(s.id)).map(s => {
    let clientName = '';
    if (s.clientId && clients) {
      const client = clients.find(c => c.id === s.clientId);
      if (client) {
        clientName = client.firstName && client.lastName
          ? `${client.firstName} ${client.lastName}`
          : client.firstName || s.clientId;
      } else {
        clientName = s.clientId;
      }
    }
    return {
      value: s.id,
      text: clientName ? `${getName(s)} [${clientName}]` : getName(s),
    };
  });
}

const mapStateToProps = (state, ownProps) => {
  const {id} = ownProps.match.params;
  const schoolClass =
    id === "new" || state.schoolClasses.length === 0
      ? { isRecurring: true}
      : getById(state.schoolClasses, id);

  const scheduledLessons = API.generateScheduledClasses(schoolClass, state.lessonRegister, state.students, state.classStudents);

  const studentsInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];
  const studentsNotInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsNotInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];

  return {
    schoolClass,
    schoolClasses: state.schoolClasses,
    teachers: state.teachers,
    students: state.students,
    books: state.books,
    scheduledLessons,
    classStudents: state.classStudents,
    studentsInThisClass,
    studentsNotInThisClass,
    lessonRegister: state.lessonRegister,
    clients: state.clients,
    activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
    userRole: state.user.role,
    userTeacherId: state.user.teacherId,
  };
};

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadClassStudents: classStudentsActions.loadClassStudents,
  addClassStudent: classStudentsActions.addClassStudent,
  removeClassStudent: classStudentsActions.removeClassStudent,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadBooks: bookActions.loadBooks,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
  loadClients: clientActions.loadClients,
};

export default connect(mapStateToProps, mapDispatchToProps)(SchoolClass);
